import { Currency } from 'constants/currencies'
import { useCurrencies } from 'contexts'
import {
  DynamicPricePicker,
  useDynamicPricePicker,
} from 'hooks/useDynamicPricePicker'
import { useUserCountry } from 'hooks/useUserCountry'
import { useUserRegion } from 'hooks/useUserRegion'
import { createContext, PropsWithChildren } from 'react'
import { Region } from 'types'

interface State {
  userRegion: Region | null
  userCountry: string | null
  userCurrency: Currency
  pricePicker: DynamicPricePicker
}

export const DynamicPricesContext = createContext<State>({} as State)

/**
 * Use the provider to avoid creating the references every time we need a helper.
 *
 * Eg. if you call `useDynamicPricePicker` directly, the callbacks inside the hook will be instantiated for every call of the hook. Using the provider guarantees that the callbacks will be instantiated once, and reinstantiated when their dependencies change.
 */
export function DynamicPricesProvider({ children }: PropsWithChildren) {
  const { userCountry } = useUserCountry()
  const { userRegion } = useUserRegion()
  const { userCurrency } = useCurrencies()
  const pricePicker = useDynamicPricePicker()

  return (
    <DynamicPricesContext.Provider
      value={{ userRegion, userCountry, userCurrency, pricePicker }}
    >
      {children}
    </DynamicPricesContext.Provider>
  )
}
